import { useOktaAuth } from '@okta/okta-react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useAppContext } from '../utils/hooks/useAppContext';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

const DEFAULT_PAGE_SIZE = 50;

type TGetPracticesParams = {
  practice?:
    | {
        name?: string | undefined;
      }
    | undefined;
  cleanClaims?: boolean | undefined;
  hideInSwitchboard?: boolean | undefined;
  meta?:
    | {
        pagination: {
          cursor?: ['id' | 'name', unknown, 'ASC' | 'DESC'][] | undefined;
          limit?: number | undefined;
        };
        sort?: Partial<Record<'id' | 'name', 'ASC' | 'DESC'>> | undefined;
      }
    | undefined;
};

type TGetPracticesResponse = {
  data: {
    id: string;
    name: string;
    cleanClaims: boolean;
  }[];
  nextCursor?:
    | {
        id: string;
        name: string;
        cleanClaims: boolean;
      }
    | undefined;
};

export const useGetPractices = (args: TGetPracticesParams) => {
  const { user } = useAppContext();
  const { authState } = useOktaAuth();
  const organizationId = user.primaryOrganizationId;
  const path = `/v2/${endpoints.organizations}/${organizationId}/${endpoints.practices}`;

  return useInfiniteQuery(
    [
      endpoints.organizations,
      'v2',
      organizationId,
      endpoints.practices,
      args.practice?.name,
      args.cleanClaims,
      args.hideInSwitchboard,
    ],
    ({ pageParam }) =>
      makeAPIRequestReactQuery<TGetPracticesResponse>(path, {
        ...args,
        meta: {
          pagination: {
            cursor: pageParam,
            limit: args.meta?.pagination?.limit || DEFAULT_PAGE_SIZE,
          },
        },
      }),
    {
      enabled: !!organizationId && !!authState?.isAuthenticated,
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
      keepPreviousData: true,
    },
  );
};
